import React from "react";

/**
 * The backend reports two different things in one `warnings` list: a model that does not
 * join (a satellite missing a hub key, keys out of order), and changes the save already
 * carried out for you — a hub's dropped key taken off its satellites and links, keys moved to
 * follow the hub. Every one of the latter ends by naming the table that needs reloading (or
 * says why a key was kept), and none of them means something is broken. Shown under "not
 * joining yet", the cascade of a perfectly good hub edit read as an error.
 */
const FOLLOW_UP = /needs reloading|stays a business key/i;

export function groupSaveWarnings(warnings: string[]): {
  notJoining: string[];
  alsoUpdated: string[];
} {
  return {
    notJoining: warnings.filter((warning) => !FOLLOW_UP.test(warning)),
    alsoUpdated: warnings.filter((warning) => FOLLOW_UP.test(warning))
  };
}

function WarningGroup({ title, items }: { title: string; items: string[] }): JSX.Element | null {
  if (!items.length) {
    return null;
  }
  return (
    <div className="tw-w-full tw-max-w-[20rem] tw-rounded-2xl tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-left">
      <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-amber-700">
        {title}
      </p>
      <ul className="tw-mt-1 tw-list-disc tw-space-y-1 tw-pl-4">
        {items.map((warning) => (
          <li key={warning} className="tw-text-xs tw-text-amber-800">
            {warning}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ColumnMappingActionCard({
  saveBehaviorText,
  onClose,
  onSave,
  saveLabel,
  isSaving,
  disabled,
  errorMessage,
  successMessage,
  warnings
}: {
  saveBehaviorText: string;
  onClose: () => void;
  onSave: () => void;
  saveLabel: string;
  isSaving: boolean;
  disabled: boolean;
  errorMessage?: string | null;
  successMessage?: string | null;
  /**
   * What the backend said about a save that went through: either the model is not coherent
   * yet — e.g. a hub gained a business key and its satellites do not carry it, so nothing
   * joins until each one answers it — or what the save changed elsewhere and which tables now
   * need reloading. Neither is an error; see groupSaveWarnings.
   */
  warnings?: string[] | null;
}): JSX.Element {
  const { notJoining, alsoUpdated } = groupSaveWarnings(warnings ?? []);
  return (
    <>
      <div className="tw-w-full tw-max-w-[20rem] tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-3 tw-text-right">
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.22em] tw-text-slate-500">
          Save behavior
        </p>
        <p className="tw-mt-1 tw-text-xs tw-text-slate-600">{saveBehaviorText}</p>
        <div className="tw-mt-3 tw-flex tw-justify-end tw-gap-2">
          <button
            type="button"
            onClick={onClose}
            className="tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 tw-transition hover:tw-bg-slate-100"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={disabled}
            className="tw-rounded-full tw-bg-[#03234a] tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#02172f] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            {isSaving ? "Saving..." : saveLabel}
          </button>
        </div>
      </div>
      {errorMessage ? <p className="tw-text-xs tw-font-medium tw-text-rose-600">{errorMessage}</p> : null}
      {successMessage ? (
        <p className="tw-text-xs tw-font-medium tw-text-emerald-600">{successMessage}</p>
      ) : null}
      <WarningGroup title="Saved, but not joining yet" items={notJoining} />
      <WarningGroup title="Saved — also updated" items={alsoUpdated} />
    </>
  );
}
